import { useState } from "react";

export interface ManualDiscount {
  description: string;
  amountCents: number;
}

interface DiscountLineInputProps {
  discounts: ManualDiscount[];
  onChange: (discounts: ManualDiscount[]) => void;
}

const MAX_DISCOUNTS = 5;

export function DiscountLineInput({ discounts, onChange }: DiscountLineInputProps) {
  const [touched, setTouched] = useState<Record<number, { description?: boolean; amount?: boolean }>>({});
  // Local string state for amount inputs to prevent focus loss during typing
  const [localAmountStr, setLocalAmountStr] = useState<Record<number, string>>({});

  const handleAddDiscount = () => {
    if (discounts.length >= MAX_DISCOUNTS) return;
    onChange([...discounts, { description: "", amountCents: 0 }]);
  };

  const handleRemoveDiscount = (index: number) => {
    const newDiscounts = discounts.filter((_, i) => i !== index);
    onChange(newDiscounts);
    // Clean up touched state for removed index
    const newTouched = { ...touched };
    delete newTouched[index];
    setTouched(newTouched);
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const newDiscounts = [...discounts];
    newDiscounts[index] = { ...newDiscounts[index], description: value };
    onChange(newDiscounts);
  };

  const handleAmountChange = (index: number, value: string) => {
    // Only update local string state during typing - don't convert to cents yet
    setLocalAmountStr((prev) => ({ ...prev, [index]: value }));
  };

  const handleAmountFocus = (index: number) => {
    // Initialize local state from current cents value when focusing
    const currentCents = discounts[index]?.amountCents || 0;
    if (currentCents > 0) {
      setLocalAmountStr((prev) => ({ 
        ...prev, 
        [index]: (currentCents / 100).toFixed(2) 
      }));
    } else {
      setLocalAmountStr((prev) => ({ ...prev, [index]: "" }));
    }
  };

  const handleAmountBlur = (index: number) => {
    // Convert local string to cents and update parent state
    const localValue = localAmountStr[index] || "";
    const cleaned = localValue.replace(/[^\d.]/g, "");
    const dollars = parseFloat(cleaned || "0");
    const cents = Math.round(dollars * 100);
    
    const newDiscounts = [...discounts];
    newDiscounts[index] = { ...newDiscounts[index], amountCents: cents };
    onChange(newDiscounts);
    
    // Clear local state and mark as touched
    setLocalAmountStr((prev) => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
    
    setTouched((prev) => ({
      ...prev,
      [index]: { ...prev[index], amount: true },
    }));
  };

  const handleBlur = (index: number, field: "description" | "amount") => {
    setTouched((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: true },
    }));
  };

  const getFieldError = (index: number, field: "description" | "amount"): string | undefined => {
    const discount = discounts[index];
    if (!discount) return undefined;

    const isTouched = touched[index]?.[field];
    if (!isTouched) return undefined;

    if (field === "description" && !discount.description.trim()) {
      return "Description required";
    }
    if (field === "amount" && discount.amountCents <= 0) {
      return "Amount must be greater than 0";
    }
    return undefined;
  };

  const formatAmount = (cents: number): string => {
    return (cents / 100).toFixed(2);
  };

  return (
    <div className="manual-discounts-section">
      <div className="manual-discounts-header">
        <h3 className="manual-discounts-title">Manual Discounts</h3>
        {discounts.length < MAX_DISCOUNTS && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleAddDiscount}
          >
            + Add Discount
          </button>
        )}
      </div>

      {discounts.length > 0 && (
        <div className="manual-discounts-list">
          {discounts.map((discount, index) => {
            const descError = getFieldError(index, "description");
            const amountError = getFieldError(index, "amount");

            return (
              <div key={index} className="discount-line-row">
                <div className="discount-line-inputs">
                  <div className={`form-group ${descError ? "has-error" : ""}`}>
                    <input
                      type="text"
                      placeholder="e.g. Loyalty discount"
                      value={discount.description}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      onBlur={() => handleBlur(index, "description")}
                      aria-label="Discount description"
                      className="discount-description-input"
                    />
                    {descError && <span className="field-error">{descError}</span>}
                  </div>

                  <div className={`form-group ${amountError ? "has-error" : ""}`}>
                    <div className="discount-amount-input-wrapper">
                      <span className="discount-amount-prefix">$</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={
                          localAmountStr[index] !== undefined 
                            ? localAmountStr[index]
                            : discount.amountCents > 0 
                            ? formatAmount(discount.amountCents) 
                            : ""
                        }
                        onChange={(e) => handleAmountChange(index, e.target.value)}
                        onFocus={() => handleAmountFocus(index)}
                        onBlur={() => handleAmountBlur(index)}
                        aria-label="Discount amount (ex GST)"
                        className="discount-amount-input"
                      />
                      <span className="discount-amount-suffix">ex GST</span>
                    </div>
                    {amountError && <span className="field-error">{amountError}</span>}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-remove-discount"
                  onClick={() => handleRemoveDiscount(index)}
                  aria-label="Remove discount"
                  title="Remove discount"
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>
      )}

      {discounts.length === 0 && (
        <p className="manual-discounts-empty">
          No manual discounts added. Click "Add Discount" to create a custom discount line.
        </p>
      )}
    </div>
  );
}
